/**
 * @deprecated Legacy public landing page (Cosmos v1).
 *
 * As of the Cosmos v2 cutover, the canonical landing at "/" is served by
 * `landing-page/App.tsx` (new design). This file is no longer routed to by
 * default — it remains mounted at "/home-legacy" purely as a rollback path
 * in case we need to revert. Once v2 is fully validated, this file (and
 * its imports) can be safely deleted.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Header from './components/Header';
import Banner from './components/Banner';
import ChatSection from './components/ChatSection';
import FeaturingSection from './components/FeaturingSection';
import ValuePropsSection from './components/ValuePropsSection';
import AnalyticsSection from './components/AnalyticsSection';
import Footer from './components/Footer';
import LoginModal from './components/LoginModal';
import RequestInviteModal from './components/RequestInviteModal';
import SignUpModal from './components/SignUpModal';
import SupportModal from './components/SupportModal';
import { PrivacyPolicyModal } from './components/PrivacyPolicyModal';
import { TermsOfServiceModal } from './components/TermsOfServiceModal';
import { SignupRequiredModal } from '../components/SignupRequiredModal';
import { OAuthSignupProgressOverlay } from '../components/OAuthSignupProgressOverlay';

import { ChatMessage, Sender, Attachment, ModalType } from '../types';
import { apiService, StreamStatus } from '../services/apiService';
import { FALLBACK_SAMPLE_PROMPTS } from '../constants';
import { trackClick } from '../utils/analytics';

interface LandingAppProps {
  onLogin: (email: string) => void;
  onNavigateToAbout?: () => void;
}

// ==================== BANNER CONFIGURATION ====================
// Toggle banner visibility: set to true to show, false to hide
const SHOW_BANNER = true;
const BANNER_MESSAGE = (
  <>
    UniversityTech.io is co-hosting a global R&amp;D Symposium in association with PitchMatter and MarketMate.{" "}
    <a
      href="https://bbzur8.short.gy/rnddubai"
      target="_blank"
      rel="noopener noreferrer"
      className="underline font-semibold"
      onClick={() => trackClick('pre_login_click_banner_cta')}
    >
      Click here for details.
    </a>
  </>
);
const BANNER_TYPE: 'info' | 'warning' | 'success' | 'error' = 'info';
const BANNER_DISMISSIBLE = true;  // Allow users to close the banner
// ==============================================================

// ==================== IMAGE BANNER & POPUP CONFIG ====================
const SHOW_IMAGE_BANNER = false;       // disable inline image banner in header
const SHOW_IMAGE_BANNER_POPUP = false; // disable scroll-triggered popup
const IMAGE_BANNER_LINK = "https://pitch.best/rd-symposium";
const IMAGE_BANNER_SCROLL_THRESHOLD = 400; // px scrolled before popup shows
// ====================================================================

const App: React.FC<LandingAppProps> = ({ onLogin, onNavigateToAbout }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRequestInviteModal, setShowRequestInviteModal] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [showSignupRequiredModal, setShowSignupRequiredModal] = useState(false);
  const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = useState(false);
  const [showTermsOfServiceModal, setShowTermsOfServiceModal] = useState(false);
  const [showOAuthSignupOverlay, setShowOAuthSignupOverlay] = useState(false);
  const chatSectionRef = useRef<HTMLDivElement>(null);

  const onOAuthSignupPhaseChange = useCallback((busy: boolean) => {
    setShowOAuthSignupOverlay(busy);
  }, []);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [samplePrompts, setSamplePrompts] = useState(FALLBACK_SAMPLE_PROMPTS);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  // Streaming state for status updates
  const [streamingStatus, setStreamingStatus] = useState<StreamStatus | null>(null);
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null);
  const [streamingResultsCount, setStreamingResultsCount] = useState<number>(0);

  // Image banner popup state
  const [showBannerPopup, setShowBannerPopup] = useState(false);
  const [bannerPopupDismissed, setBannerPopupDismissed] = useState(false);

  useEffect(() => {
    // Use random predefined questions instead of API calls
    import('../constants').then(({ getRandomLoginSuggestions }) => {
      setSamplePrompts(getRandomLoginSuggestions());
    });
  }, []);

  // Scroll-triggered banner popup
  useEffect(() => {
    if (!SHOW_IMAGE_BANNER_POPUP || bannerPopupDismissed) return;

    // Check if already dismissed this session
    if (sessionStorage.getItem('banner_popup_dismissed') === 'true') {
      setBannerPopupDismissed(true);
      return;
    }

    const handleScroll = () => {
      if (window.scrollY > IMAGE_BANNER_SCROLL_THRESHOLD && !bannerPopupDismissed) {
        setShowBannerPopup(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [bannerPopupDismissed]);

  const dismissBannerPopup = () => {
    setShowBannerPopup(false);
    setBannerPopupDismissed(true);
    sessionStorage.setItem('banner_popup_dismissed', 'true');
  };

  // Check for OAuth (LinkedIn/Google) signup redirect and open signup modal
  useEffect(() => {
    const checkSignupMode = () => {
      const isSignupMode = sessionStorage.getItem('clerk_signup_mode') === 'true';
      if (isSignupMode) {
        // Close other modals and open signup modal
        setShowLoginModal(false);
        setShowRequestInviteModal(false);
        setShowSupportModal(false);
        setShowSignUpModal(true);
      }
    };

    checkSignupMode();
    // One quick retry for Clerk hydration (500ms was noticeably sluggish on mobile)
    const timeoutId = setTimeout(checkSignupMode, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  const handleSendMessage = async (prompt: string, files: File[] = []) => {
    setIsLoading(true);

    // Check if this message is from one of the prompt cards
    const isFromPromptCard = samplePrompts.some(promptCard => promptCard.prompt === prompt);
    const isBenchmarkPrompt = samplePrompts.some(promptCard => 
      promptCard.title === "Benchmarking technologies" && promptCard.prompt === prompt
    );
    if (isFromPromptCard) {
      trackClick('pre_login_click_sample_prompt', { prompt });
    }
    console.log(`🎯 Landing page - Message from prompt card: ${isFromPromptCard}, Benchmark: ${isBenchmarkPrompt}`, { prompt, samplePrompts });

    // Add prefix for benchmark prompts
    let processedPrompt = prompt;
    if (isBenchmarkPrompt) {
      processedPrompt = `I want to benchmark: ${prompt}`;
    }

    const attachments: Attachment[] = files.map(file => ({
        name: file.name,
        type: file.type,
        url: URL.createObjectURL(file),
        size: file.size,
    }));

    const userMessage: ChatMessage = { 
        id: Date.now().toString(), 
        text: prompt,  // Display original prompt to user
        sender: Sender.User, 
        attachments 
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      // Reset streaming state
      setStreamingStatus(null);
      setStreamingMessage(null);
      setStreamingResultsCount(0);
      
      // Landing page (anonymous) always uses the default LanceDB endpoint.
      // The Algolia variant is internal-only — see utils/featureFlags.ts.
      console.log('🔍 Landing Page API Debug:', {
        endpoint: '/api/v1/conversation/process/v2/stream',
        extensiveSearch: true,
        topN: 7
      });
      
      // Use the FastAPI conversation streaming endpoint for landing page
      const response = await apiService.processConversationV2Stream(
        processedPrompt,  // Use processed prompt with prefix for backend
        chatSessionId, // Will be null for first message
        null, // No user_id for anonymous users on landing page
        files,
        {
          // Disable follow-ups for non-benchmark prompt cards, enable for benchmark
          enableFollowups: !isFromPromptCard || isBenchmarkPrompt,
          maxFollowups: (isFromPromptCard && !isBenchmarkPrompt) ? 0 : 1,  // Landing page: 1 followup
          extensiveSearch: true,  // Enable extensive search
          topN: 10  // Default to 10, same as main application
        },
        // Status update callback for streaming
        (update) => {
          setStreamingStatus(update.status);
          setStreamingMessage(update.message);
          setStreamingResultsCount(update.results_count);
          console.log(`🔄 Stream: ${update.status} - ${update.message} (${update.results_count} results)`);
        }
      );

      if (response.success && response.data) {
        const { data } = response;
        
        // Update session ID if returned (for subsequent messages)
        if (data.session_id && !chatSessionId) {
          setChatSessionId(data.session_id);
        }

        let aiResponseText = '';
        
        if (data.type === 'followup') {
          // Handle follow-up question
          aiResponseText = data.question || 'Let me ask you a follow-up question...';
        } else if (data.type === 'search_results') {
          // Handle search results with summary
          if (data.summary) {
            aiResponseText = data.summary;
          } else if (data.results && data.results.length > 0) {
            // Fallback: create a summary from results
            aiResponseText = `I found ${data.results.length} relevant results for your query. Here are some key findings that might interest you...`;
          } else {
            aiResponseText = "I searched through university innovations but couldn't find specific matches for your query. Could you try rephrasing or being more specific?";
          }
        } else if (data.type === 'out_of_scope' || data.type === 'out_of_coverage') {
          // Handle out of scope/coverage queries
          aiResponseText = data.response || 
            ((data.type as string) === 'out_of_coverage' 
              ? "I specialize in US, Indian, German, Canada university technology transfer. The university or region you're asking about isn't in my current database. I can help you find similar technologies from MIT, Stanford, Columbia, Cornell, Johns Hopkins, UCLA, Purdue, University of Arizona, University of Minnesota, University of Wisconsin-Madison, Indian Institute of Technology Bombay (IITB), Indian Institute of Technology Madras (IITM), Indian Institute of Technology Delhi (IITD), Agrinnovate, Arizona State University, Max Planck Innovation, Karlsruhe Institute of Technology (KIT), RWTH Aachen University, Durham University, Newcastle University, University of Pittsburgh, Oxford University, State University of New York, University of Toronto, University of Pennsylvania, University of Maryland, University of California San Diego, Council of Scientific & Industrial Research (CSIR) instead."
              : "I'm specialized in US, Indian, German, Canada university technology transfer and research partnerships. Could you ask about university innovations, research collaborations, or technology licensing instead?");
        } else {
          // Handle unexpected response format
          aiResponseText = data.summary || data.question || data.response || "I received an unexpected response format. Please try again.";
        }

        // Create AI message with search results as attachments if available
        const aiMessageAttachments: any[] = [];
        if (data.type === 'search_results' && data.results) {
          // Add search results as an attachment so they can be rendered
          aiMessageAttachments.push({
            search_results: data.results
          });
        }

        const aiMessage: ChatMessage = { 
          id: (Date.now() + 1).toString(), 
          text: aiResponseText, 
          sender: Sender.AI,
          attachments: aiMessageAttachments.length > 0 ? aiMessageAttachments : undefined
        };
        setMessages(prev => [...prev, aiMessage]);
      } else {
        // Handle API errors - show the error message to the user
        console.warn('API call failed:', response.error);
        
        // Extract error message
        let errorMessage = "I encountered an error processing your request. Please try again.";
        
        if (typeof response.error === 'string') {
          // Use the error message directly from the API
          errorMessage = response.error;
        } else if (typeof response.error === 'object' && response.error && 'message' in response.error) {
          errorMessage = (response.error as any).message;
        } else if (response.status === 429) {
          // Rate limit error
          errorMessage = "You've reached the chat limit for anonymous users. Please log in for unlimited access.";
        }
        
        const errorAiMessage: ChatMessage = { 
          id: (Date.now() + 1).toString(), 
          text: errorMessage, 
          sender: Sender.AI 
        };
        setMessages(prev => [...prev, errorAiMessage]);
      }
    } catch (error) {
      // Handle network or other errors
      console.error('Error processing request:', error);
      const errorMessage = error instanceof Error 
        ? `I encountered an error: ${error.message}. Please try again or log in for full access.`
        : "I encountered an unexpected error. Please try again or log in for full access.";
      
      const errorAiMessage: ChatMessage = { 
        id: (Date.now() + 1).toString(), 
        text: errorMessage, 
        sender: Sender.AI 
      };
      setMessages(prev => [...prev, errorAiMessage]);
    } finally {
      setIsLoading(false);
      // Clear streaming state
      setStreamingStatus(null);
      setStreamingMessage(null);
      setStreamingResultsCount(0);
    }
  };

  const handleResetChat = () => {
    setMessages([]);
    setChatSessionId(null); // Clear session ID to start fresh
  };

  const handlePromptClick = (prompt: string) => {
    handleSendMessage(prompt, []);
    chatSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleOpenLogin = () => {
    trackClick('pre_login_click_login_inline');
    setShowRequestInviteModal(false);
    setShowSupportModal(false);
    setShowLoginModal(true);
  };

  const handleOpenRequestInvite = () => {
    setShowLoginModal(false);
    setShowSupportModal(false);
    setShowSignUpModal(false);
    setShowRequestInviteModal(true);
  };

  const handleOpenSignUp = () => {
    trackClick('pre_login_click_signup_inline');
    setShowLoginModal(false);
    setShowSupportModal(false);
    setShowRequestInviteModal(false);
    setShowSignUpModal(true);
  };

  const handleSignUpSuccess = (email: string) => {
    // After successful signup, redirect to login or auto-login
    setShowSignUpModal(false);
    setShowLoginModal(true);
    // Optionally, you could auto-login the user here
  };
  


  const handleOpenSupport = () => {
    setShowLoginModal(false);
    setShowRequestInviteModal(false);
    setShowSupportModal(true);
  };

  const handleOpenSupportModal = (type: ModalType) => {
    if (type === ModalType.BookCall) {
        handleOpenSupport();
    }
  };


  const handleCloseModals = () => {
    setShowLoginModal(false);
    setShowRequestInviteModal(false);
    setShowSignUpModal(false);
    setShowSupportModal(false);
    setPasswordResetSuccess(false);
    setShowSignupRequiredModal(false);
    setShowOAuthSignupOverlay(false);
    setShowPrivacyPolicyModal(false);
    setShowTermsOfServiceModal(false);
  };

  const handleShowPrivacyPolicy = () => {
    setShowPrivacyPolicyModal(true);
  };

  const handleShowTermsOfService = () => {
    setShowTermsOfServiceModal(true);
  };

  const handleShowAboutUs = () => {
    trackClick('pre_login_click_about_us');
    if (onNavigateToAbout) {
      onNavigateToAbout();
    }
  };

  const handleReadMoreClick = () => {
    setShowSignupRequiredModal(true);
  };

  const handleExploreClick = () => {
    chatSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-[#FCFBF9] min-h-screen font-space-grotesk text-grayscale-1200 overflow-x-hidden">
      {showOAuthSignupOverlay && <OAuthSignupProgressOverlay />}
      {showLoginModal && <LoginModal onClose={handleCloseModals} onLogin={onLogin} onSignUpClick={handleOpenSignUp} />}
      {showRequestInviteModal && <RequestInviteModal onClose={handleCloseModals} />}
      {showSignUpModal && (
        <SignUpModal
          onClose={handleCloseModals}
          onSignupSuccess={handleSignUpSuccess}
          onOAuthSignupPhaseChange={onOAuthSignupPhaseChange}
        />
      )}
      {showSupportModal && <SupportModal onClose={handleCloseModals} />}
      {showSignupRequiredModal && (
        <SignupRequiredModal
          isOpen={showSignupRequiredModal}
          onClose={handleCloseModals}
          onLogin={handleOpenLogin}
          onSignup={handleOpenSignUp}
        />
      )}
      
      {/* Legal Modals */}
      {showPrivacyPolicyModal && (
        <PrivacyPolicyModal
          isOpen={showPrivacyPolicyModal}
          onClose={handleCloseModals}
        />
      )}
      {showTermsOfServiceModal && (
        <TermsOfServiceModal
          isOpen={showTermsOfServiceModal}
          onClose={handleCloseModals}
        />
      )}
      
      {/* Password Reset Success Message */}
      {passwordResetSuccess && (
        <div className="fixed inset-x-0 top-0 z-50 flex justify-center pt-4 sm:pt-6 pointer-events-none">
          <div className="pointer-events-auto p-4 bg-green-100 border border-green-300 rounded-lg shadow-lg max-w-sm">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">Password Reset Successful!</p>
                <p className="text-xs text-green-700 mt-1">Please login with your new password.</p>
              </div>
              <button 
                onClick={() => setPasswordResetSuccess(false)}
                className="ml-auto text-green-500 hover:text-green-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Banner - Configurable at top of file */}
      <Banner 
        message={BANNER_MESSAGE}
        type={BANNER_TYPE}
        isVisible={SHOW_BANNER}
        dismissible={BANNER_DISMISSIBLE}
      />

      {/* Scroll-triggered image banner popup - closes only via close button */}
      {showBannerPopup && !bannerPopupDismissed && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{
            backgroundColor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(4px)',
            animation: 'bannerFadeIn 0.3s ease-out'
          }}
        >
          <div
            className="relative w-[90vw] max-w-lg sm:max-w-xl md:max-w-2xl"
            style={{ animation: 'bannerScaleIn 0.3s ease-out' }}
          >
            {/* Close button - only way to close the popup */}
            <button
              onClick={dismissBannerPopup}
              className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 z-10 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full bg-white shadow-lg border border-gray-200 hover:bg-gray-100 transition-colors"
              aria-label="Close banner"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            {/* Banner image - landscape for desktop, portrait for mobile */}
            <a
              href={IMAGE_BANNER_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl overflow-hidden shadow-2xl"
            >
              {/* Desktop: landscape */}
              <img
                src="/Banner/9_16.png"
                alt="R&D Symposium 2026 - 22-24 April, Dubai"
                className="hidden sm:block w-full h-auto rounded-xl"
              />
              {/* Mobile: portrait */}
              <img
                src="/Banner/4_5.png"
                alt="R&D Symposium 2026 - 22-24 April, Dubai"
                className="block sm:hidden w-full h-auto max-h-[80vh] object-contain rounded-xl"
              />
            </a>
          </div>
          <style>{`
            @keyframes bannerFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes bannerScaleIn {
              from { opacity: 0; transform: scale(0.9); }
              to { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}
      
      <div className="max-w-screen-xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 w-full overflow-x-hidden">
        <Header onLoginClick={handleOpenLogin} onRequestInviteClick={handleOpenRequestInvite} onSignUpClick={handleOpenSignUp} onSupportClick={handleOpenSupport} showBanner={SHOW_IMAGE_BANNER} />
        <main className="mt-4 sm:mt-6 md:mt-8 w-full" ref={chatSectionRef}>
          <ChatSection 
            messages={messages}
            onSend={handleSendMessage}
            isLoading={isLoading}
            openModal={handleOpenSupportModal}
            samplePrompts={samplePrompts}
            onLoginClick={handleOpenLogin}
            onSignUpClick={handleOpenSignUp}
            onReset={handleResetChat}
            onReadMore={handleReadMoreClick}
            streamingStatus={streamingStatus}
            streamingMessage={streamingMessage}
            streamingResultsCount={streamingResultsCount}
          />
        </main>
      </div>
      <div className="mt-8 sm:mt-12 flex flex-col items-center w-full overflow-x-hidden">
        <ValuePropsSection onPromptClick={handlePromptClick} onLoginClick={handleOpenLogin} />
        <AnalyticsSection />
        <FeaturingSection />
        <div className="w-full max-w-6xl px-2 sm:px-4 md:px-5">
            <div className="w-full h-[0.25px] bg-primary-700 my-8 sm:my-12 md:my-16"></div>
        </div>
      </div>
      <Footer onShowPrivacyPolicy={handleShowPrivacyPolicy} onShowTermsOfService={handleShowTermsOfService} onShowAboutUs={handleShowAboutUs} />
    </div>
  );
};

export default App;